import { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MusicNote } from '@phosphor-icons/react';
import AddSongDialog from '@/components/AddSongDialog';
import EditSongDialog from '@/components/EditSongDialog';

function getSongPreviewSrc(data) {
  if (!data) return '';

  if (data.soundcloud_embed_html) {
    const match = data.soundcloud_embed_html.match(
      /src=["']([^"']+)["']/i
    );

    if (match?.[1]) {
      return match[1];
    }
  }

  if (data.soundcloud_url) {
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
      data.soundcloud_url
    )}&color=%23ccff00&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`;
  }

  if (data.spotify_url) {
    const match = data.spotify_url.match(
      /open\.spotify\.com\/(track|album)\/([A-Za-z0-9]+)/
    );

    if (match) {
      return `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=tuneamplifier`;
    }
  }

  if (data.youtube_url) {
    const match = data.youtube_url.match(
      /(?:youtu\.be\/|youtube\.com\/watch\?v=)([A-Za-z0-9_-]+)/
    );

    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }

  return '';
}

export default function SongPicker({
  songs = [],
  playlists = [],
  selectedSongId = '',
  onSongChange,
  playlistId = '',
  songSort,
  onSongSortChange,
  onAddSong,
  onEditSong,
  onRemoveSong,
  onRemoveMultiple,
  manageSongs = false,
  requireAudio = false,
  onSongsChange,
}) {
  const [internalSongSort, setInternalSongSort] = useState('title');
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  const [showRemoveSongs, setShowRemoveSongs] = useState(false);
  const [removingSongs, setRemovingSongs] = useState(false);
  const [showAddSong, setShowAddSong] = useState(false);
  const [showEditSong, setShowEditSong] = useState(false);

  const visibleSongs = requireAudio
    ? songs.filter(song => Boolean(song?.audio_url))
    : songs;

  const effectiveSongSort =
    songSort === undefined ? internalSongSort : songSort;

  const selectedSong = visibleSongs.find(
    song => song.song_id === selectedSongId
  ) || null;

  const setSort = value => {
    if (onSongSortChange) {
      onSongSortChange(value);
    } else {
      setInternalSongSort(value);
    }
  };

  const playlistNamesForSong = id =>
    playlists
      .filter(p => (p.song_ids || []).includes(id))
      .map(p => p.name || '')
      .filter(Boolean)
      .sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      )
      .join(' · ');

  const playlistMetaForSong = id => {
    const playlist = playlists.find(
      p => p.playlist_id === playlistId
    );

    if (!playlist) return null;

    const meta = (playlist.song_metadata || []).find(
      item => item.song_id === id
    );

    if (meta) return meta;

    const index = (playlist.song_ids || []).indexOf(id);

    return index >= 0
      ? { song_id: id, position: index }
      : null;
  };

  const sortedSongs = [...visibleSongs].sort((a, b) => {
    switch (effectiveSongSort) {
      case 'playlist':
        return playlistNamesForSong(a.song_id).localeCompare(
          playlistNamesForSong(b.song_id),
          undefined,
          { sensitivity: 'base' }
        );

      case 'artist':
        return (a.artist || '').localeCompare(
          b.artist || '',
          undefined,
          { sensitivity: 'base' }
        );

      case 'release_date':
        return (b.release_date || '').localeCompare(
          a.release_date || ''
        );

      case 'added_at': {
        const am = playlistMetaForSong(a.song_id);
        const bm = playlistMetaForSong(b.song_id);

        return (bm?.added_at || '').localeCompare(
          am?.added_at || ''
        );
      }

      case 'playlist_order': {
        const am = playlistMetaForSong(a.song_id);
        const bm = playlistMetaForSong(b.song_id);

        return (
          (am?.position ?? 999999999) -
          (bm?.position ?? 999999999)
        );
      }

      case 'title':
      default:
        return (a.title || '').localeCompare(
          b.title || '',
          undefined,
          { sensitivity: 'base' }
        );
    }
  });

  const toggleSongSelection = id => {
    setSelectedSongIds(current =>
      current.includes(id)
        ? current.filter(x => x !== id)
        : [...current, id]
    );
  };

  const toggleAllSongSelection = () => {
    const ids = sortedSongs.map(song => song.song_id);

    if (
      ids.length &&
      ids.every(id => selectedSongIds.includes(id))
    ) {
      setSelectedSongIds([]);
    } else {
      setSelectedSongIds(ids);
    }
  };

  const refreshSongs = async () => {
    const { data } = await api.get('/songs');
    const nextSongs = Array.isArray(data) ? data : [];
    onSongsChange?.(nextSongs);
    return nextSongs;
  };

  const handleAddSaved = async song => {
    await refreshSongs();

    if (!requireAudio || song?.audio_url) {
      onSongChange?.(song.song_id);
    }
  };

  const handleEditSaved = async () => {
    await refreshSongs();
  };

  const deleteManagedSong = async song => {
    if (!song) return;

    const confirmed = window.confirm(
      `Remove "${song.title}" from your TuneAmplifier catalog?`
    );

    if (!confirmed) return;

    try {
      await api.delete(`/songs/${song.song_id}`);
      await refreshSongs();

      if (selectedSongId === song.song_id) {
        onSongChange?.('');
      }

      toast.success(`Removed "${song.title}"`);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not remove song'
      );
    }
  };

  const removeManagedSongs = async () => {
    const ids = [...new Set(selectedSongIds)];

    if (!ids.length) {
      toast.error('Select at least one song');
      return;
    }

    const confirmed = window.confirm(
      `Remove ${ids.length} selected song${ids.length === 1 ? '' : 's'} from your catalog?`
    );

    if (!confirmed) return;

    setRemovingSongs(true);

    try {
      const { data } = await api.post('/songs/bulk-delete', {
        song_ids: ids,
      });

      await refreshSongs();

      if (ids.includes(selectedSongId)) {
        onSongChange?.('');
      }

      setSelectedSongIds([]);
      setShowRemoveSongs(false);

      toast.success(
        `Removed ${data.deleted_song_count || 0} song${(data.deleted_song_count || 0) === 1 ? '' : 's'}`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not remove selected songs'
      );
    } finally {
      setRemovingSongs(false);
    }
  };

  const handleAddClick = () => {
    if (manageSongs) {
      setShowAddSong(true);
    } else {
      onAddSong?.();
    }
  };

  const handleEditClick = () => {
    if (!selectedSong) return;

    if (manageSongs) {
      setShowEditSong(true);
    } else {
      onEditSong?.(selectedSong);
    }
  };

  const handleRemoveClick = () => {
    if (!selectedSong) return;

    if (manageSongs) {
      deleteManagedSong(selectedSong);
    } else {
      onRemoveSong?.(selectedSong);
    }
  };

  const handleRemoveMultipleClick = () => {
    if (manageSongs) {
      setShowRemoveSongs(true);
    } else {
      onRemoveMultiple?.();
    }
  };

  const selectedSongPreviewSrc = getSongPreviewSrc(selectedSong);

  return (
    <>
      <div className="flex gap-2 items-center flex-wrap">
        <Select
          value={selectedSongId}
          onValueChange={onSongChange}
        >
          <SelectTrigger
            data-testid="create-song-select"
            className="bg-zinc-950 border-zinc-800 max-w-sm"
          >
            <SelectValue
              placeholder={
                visibleSongs.length
                  ? 'Pick a song'
                  : 'No songs yet'
              }
            />
          </SelectTrigger>

          <SelectContent>
            {sortedSongs.map(song => (
              <SelectItem
                key={song.song_id}
                value={song.song_id}
              >
                {song.title} — {song.artist}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={effectiveSongSort}
          onValueChange={setSort}
        >
          <SelectTrigger className="bg-zinc-950 border-zinc-800 w-[220px]">
            <SelectValue placeholder="Sort songs" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="playlist">
              Sort by playlist
            </SelectItem>
            <SelectItem value="title">
              Sort by song name
            </SelectItem>
            <SelectItem value="artist">
              Sort by artist
            </SelectItem>
            <SelectItem value="release_date">
              Sort by release date
            </SelectItem>
            <SelectItem value="added_at">
              Sort by added to playlist
            </SelectItem>
            <SelectItem value="playlist_order">
              Sort by playlist order
            </SelectItem>
          </SelectContent>
        </Select>

        <div className="basis-full flex flex-wrap items-center gap-2">
          <div className="shrink-0 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              data-testid="create-add-song-btn"
              variant="outline"
              onClick={handleAddClick}
              className="border-zinc-800 hover:border-zinc-600"
            >
              + Add song
            </Button>

            {selectedSongId && (
              <Button
                type="button"
                variant="outline"
                onClick={handleEditClick}
                className="border-zinc-800 hover:border-[#ccff00]/60"
              >
                Edit
              </Button>
            )}
          </div>

          <div className="shrink-0 flex flex-wrap items-center gap-2">
            {selectedSongId && (
              <Button
                type="button"
                variant="outline"
                onClick={handleRemoveClick}
                className="border-red-500/40 text-red-400 hover:border-red-500/70 hover:text-red-300"
              >
                Remove
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              disabled={!visibleSongs.length}
              onClick={handleRemoveMultipleClick}
              className="border-red-500/40 text-red-400 hover:border-red-500/70 hover:text-red-300"
            >
              Remove multiple
            </Button>
          </div>
        </div>

        <div className="basis-full h-0" />

        {selectedSong && (
          <div className="basis-full border border-zinc-800 rounded-xl bg-zinc-900/40 p-4">
            <div className="flex items-center gap-3 mb-3">
              {selectedSong.cover_url ? (
                <img
                  src={selectedSong.cover_url}
                  alt=""
                  className="w-14 h-14 rounded-lg object-cover border border-zinc-800"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg border border-zinc-800 bg-zinc-950 flex items-center justify-center text-zinc-700">
                  <MusicNote size={20} />
                </div>
              )}

              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-100 truncate">
                  {selectedSong.title || 'Untitled'}
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">
                  {selectedSong.artist || 'Unknown artist'}
                </div>
              </div>
            </div>

            {selectedSong.audio_url ? (
              <audio
                key={selectedSong.song_id}
                className="w-full"
                controls
                preload="metadata"
                src={selectedSong.audio_url}
              />
            ) : selectedSongPreviewSrc ? (
              <div className="overflow-hidden rounded-lg border border-zinc-800 bg-black">
                <iframe
                  key={selectedSong.song_id}
                  title={`${selectedSong.title || 'Song'} preview`}
                  src={selectedSongPreviewSrc}
                  className="w-full h-[152px]"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  loading="eager"
                />
              </div>
            ) : (
              <div className="border border-zinc-800 rounded-lg bg-zinc-950 p-3 text-xs text-zinc-500">
                This song does not expose an embeddable player.
              </div>
            )}
          </div>
        )}
      </div>

      {manageSongs && (
        <>
          <AddSongDialog
            open={showAddSong}
            onOpenChange={setShowAddSong}
            onSaved={handleAddSaved}
          />

          <EditSongDialog
            open={showEditSong}
            onOpenChange={setShowEditSong}
            song={selectedSong}
            onSaved={handleEditSaved}
          />

          <Dialog
            open={showRemoveSongs}
            onOpenChange={setShowRemoveSongs}
          >
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display">
                  Remove multiple songs
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-zinc-500">
                    {selectedSongIds.length} selected
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={toggleAllSongSelection}
                      className="border-zinc-800"
                    >
                      Select all
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSelectedSongIds([])}
                      className="border-zinc-800"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                {sortedSongs.map(song => (
                  <label
                    key={song.song_id}
                    className="flex items-center gap-3 rounded-lg border border-zinc-800 px-3 py-2 cursor-pointer hover:border-zinc-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSongIds.includes(song.song_id)}
                      onChange={() => toggleSongSelection(song.song_id)}
                      className="accent-[#ccff00]"
                    />

                    <div className="min-w-0">
                      <div className="text-sm truncate">
                        {song.title}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {song.artist}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRemoveSongs(false)}
                  className="border-zinc-800"
                >
                  Cancel
                </Button>

                <Button
                  type="button"
                  onClick={removeManagedSongs}
                  disabled={
                    removingSongs ||
                    !selectedSongIds.length
                  }
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  {removingSongs
                    ? 'Removing…'
                    : `Remove ${selectedSongIds.length}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}
